import { apiBase } from "../../api_client";
import { tokenStore } from "../../authentication/token_store";
import type { PaginatedResponse } from "../../pagination/data/types/pagination";
import type {
  Chat,
  ChatAttachmentInput,
  ChatInsight,
  ChatMessage,
  StreamCallbacks,
} from "./types/chat";

export interface ChatsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface ChatStreamExtras {
  /** Pin the currently-viewed product into the BE catalog context. */
  productId?: number;
  /**
   * The variant the user has selected on the product page. Stock is per-variant,
   * so without this the assistant reports the template-wide total instead of the
   * number the page is showing.
   */
  variantId?: number;
}

export interface ChatRepository {
  getChats(): Promise<Chat[]>;
  getChatsPaginated(query: ChatsQuery): Promise<PaginatedResponse<Chat>>;
  getMessages(chatId: string): Promise<ChatMessage[]>;
  sendMessageStream(
    message: string,
    chatId: string | null,
    attachments: ChatAttachmentInput[] | undefined,
    callbacks: StreamCallbacks,
    signal?: AbortSignal,
    extras?: ChatStreamExtras,
  ): Promise<void>;
}

// ── Mock repository (dev / demo) ─────────────────────────────────────
// Streams a canned reply word-by-word so the shimmer + smart-scroll paths
// are exercised even with no backend.

const MOCK_REPLIES = [
  "I can help with that. Based on what you're looking for, here are some recommendations — Dell Latitude 5440 and Lenovo ThinkPad L14 both hit the budget and have strong reliability ratings in the GCC region.\n\nLet me know if you'd like me to narrow by warranty length, MOQ, or delivery window.",
  "Looking at the options in your price range, I'd highlight the HP EliteBook 840 G10 for balanced value and the Nexus Pro Mesh NX-80 for the best unit economics on volume orders over 50.\n\nWant me to pull up a side-by-side comparison?",
  "Got it. For a 200-unit order delivered to Doha within three weeks, three suppliers can meet that timeline. The bulk discount kicks in at 100 units — so you'd save roughly 12% versus list.\n\nShall I prepare a quote?",
];

// Pre-seeded showcase data — exercises every widget the backend can emit so
// /my-conversations can preview them without a live backend. Activated when
// `VITE_CHAT_MOCK=true` (or no `apiBase` is configured).
function seedTime(minutesAgo: number): string {
  return new Date(Date.now() - minutesAgo * 60_000).toISOString();
}

function seedChat(
  id: string,
  title: string,
  preview: string,
  messages: Array<Pick<ChatMessage, "role" | "text">>,
  minutesAgo: number,
): { chat: Chat; messages: ChatMessage[] } {
  const baseTs = seedTime(minutesAgo);
  return {
    chat: {
      id,
      title,
      createdAt: baseTs,
      updatedAt: baseTs,
      lastMessageAt: baseTs,
      lastMessagePreview: preview,
    },
    messages: messages.map((m, i) => ({
      id: `${id}-msg-${i}`,
      chatId: id,
      role: m.role,
      text: m.text,
      timestamp: new Date(Date.now() - (minutesAgo - i) * 60_000).toISOString(),
    })),
  };
}

const SEED = [
  seedChat(
    "seed-recommendations",
    "20 laptops for engineering",
    "Here are the best matches for your team…",
    [
      { role: "user", text: "20 laptops for a new engineering team, budget 3,500 QAR each" },
      {
        role: "assistant",
        text: 'Based on your budget and team size, here are the strongest matches.\n\n{{recommendations:{"products":[{"name":"Dell Latitude 5440","specs":"i5 · 16GB · 512GB SSD · 14\\" · Win 11 Pro","price":"QAR 680","badge":"BEST VALUE","variantId":1},{"name":"Dell Latitude 5440","specs":"i5 · 16GB · 512GB SSD · 14\\" · Win 11 Pro","price":"QAR 680","badge":"RELIABLE","variantId":2}],"alternatives":[{"name":"Dell Latitude 5440","subLabel":"LOW MOQ · 25 UNITS MIN","price":"QAR 680"},{"name":"Nexus Pro Mesh NX-80","subLabel":"BEST PRICE · 5YR WARRANTY","price":"QAR 480"},{"name":"Workline WL Series 400","subLabel":"FAST DELIVERY · 5-DAY LEAD","price":"QAR 620"}],"notes":["All options available from single fulfillment in Doha.","3 suppliers offer 10–15% bulk discount for orders over 100 units.","Bulk order qualifies for priority delivery (3–5 days).","GreenHub has lowest MOQ (25 units) to test before bulk.","Quote valid 24 hrs — one-click renewal available."]}}}\n\nHappy to dive deeper into any of these.',
      },
    ],
    5,
  ),
  seedChat(
    "seed-results",
    "Stock & MOQ check",
    "Three suppliers can meet that timeline…",
    [
      { role: "user", text: "Can three suppliers fulfil 200 units to Doha in three weeks?" },
      {
        role: "assistant",
        text: 'Here is the live availability picture for that order shape.\n\n{{results:{"title":"Doha — 200 unit window","rows":[{"label":"Suppliers in stock","value":"3","status":"high"},{"label":"Average lead time","value":"12 days","status":"normal"},{"label":"Min MOQ","value":"25 units","status":"low"},{"label":"Bulk discount tier","value":"100+ units","status":"normal"}]}}}\n\nWant me to draft a quote against the cheapest supplier?',
      },
    ],
    25,
  ),
  seedChat(
    "seed-bar-chart",
    "Price comparison across suppliers",
    "Quoted price per unit by supplier…",
    [
      { role: "user", text: "Compare price per unit across the four shortlisted suppliers" },
      {
        role: "assistant",
        text: 'Here is the per-unit quote spread for a 100-unit order.\n\n{{barChart:{"title":"Per-unit price (100 units)","unit":"QAR","maxValue":900,"points":[{"label":"GreenHub","value":640},{"label":"Nexus Pro","value":480},{"label":"Workline","value":620},{"label":"Acer Direct","value":540}]}}}\n\nNexus Pro leads on price; happy to pull warranty terms next.',
      },
    ],
    60,
  ),
  seedChat(
    "seed-line-chart",
    "Monitor pricing trend",
    "Average market price has eased ~8%…",
    [
      { role: "user", text: "How has the price of 27\" monitors trended this year?" },
      {
        role: "assistant",
        text: 'Looking at the GCC market, prices have softened across the year.\n\n{{lineChart:{"title":"27\\" monitor — average list price","unit":"QAR","maxValue":1400,"points":[{"label":"Jan","value":1280},{"label":"Feb","value":1245},{"label":"Mar","value":1210},{"label":"Apr","value":1190},{"label":"May","value":1170},{"label":"Jun","value":1140},{"label":"Jul","value":1125},{"label":"Aug","value":1180}]}}}\n\nGood window to lock in volume orders before Q4.',
      },
    ],
    120,
  ),
  seedChat(
    "seed-report-card",
    "Vendor performance snapshot",
    "Latest scorecard for our top suppliers…",
    [
      { role: "user", text: "How are our top three suppliers performing this quarter?" },
      {
        role: "assistant",
        text: 'Here is the most recent scorecard. The highlighted point is this month\'s reading.\n\n{{reportCard:{"title":"Supplier scorecard — Q3","points":[{"x":1,"y":62},{"x":2,"y":68},{"x":3,"y":71},{"x":4,"y":74},{"x":5,"y":79},{"x":6,"y":82,"latest":true}],"metrics":[{"label":"On-time rate","value":"94%"},{"label":"Defect rate","value":"0.6%"},{"label":"Avg lead time","value":"11d"},{"label":"NPS","value":"+42"}]}}}\n\nAll three are trending up — no escalations needed this cycle.',
      },
    ],
    180,
  ),
  seedChat(
    "seed-text-options",
    "Procurement strategy briefing",
    "Three options to weigh for the renewal…",
    [
      { role: "user", text: "What are our renewal options for the printing-supplies contract?" },
      {
        role: "assistant",
        text: 'Quick brief for the steering meeting.\n\n{{textOptions:{"title":"Renewal options","subtitle":"Three viable paths for FY26","paragraphs":["Continuing with the incumbent locks in current pricing for 12 months but caps our access to new SKU drops.","Splitting the contract across two suppliers improves resilience at the cost of a thin admin overhead.","Switching to the challenger unlocks a 9% headline saving but requires a one-quarter transition window."],"bullets":["Decision needed by 2026-05-15","Impacts ~140 monthly POs","Legal review already complete on all three"]}}}\n\nHappy to draft a one-pager for the board.',
      },
    ],
    300,
  ),
  seedChat(
    "seed-action",
    "Quick navigation",
    "Open the live catalog…",
    [
      { role: "user", text: "Show me the full catalog" },
      {
        role: "assistant",
        text: 'You can browse everything live here.\n\n{{action:{"label":"Open catalog","action":"navigate_products"}}}\n\nLet me know if you want me to filter by category before you head over.',
      },
    ],
    400,
  ),
  seedChat(
    "seed-everything",
    "Full procurement walkthrough",
    "End-to-end snapshot for the laptop rollout…",
    [
      { role: "user", text: "Give me a full snapshot for the laptop rollout — recs, prices, trends, vendor health, options, and next step." },
      {
        role: "assistant",
        text:
          'Here is the full picture in one place.\n\n' +
          '{{recommendations:{"products":[{"name":"Dell Latitude 5440","specs":"i5 · 16GB · 512GB SSD · 14\\" · Win 11 Pro","price":"QAR 680","badge":"BEST VALUE","variantId":1},{"name":"Dell Latitude 5440","specs":"i5 · 16GB · 512GB SSD · 14\\" · Win 11 Pro","price":"QAR 680","badge":"RELIABLE","variantId":2}],"alternatives":[{"name":"Dell Latitude 5440","subLabel":"LOW MOQ · 25 UNITS MIN","price":"QAR 680"},{"name":"Nexus Pro Mesh NX-80","subLabel":"BEST PRICE · 5YR WARRANTY","price":"QAR 480"},{"name":"Workline WL Series 400","subLabel":"FAST DELIVERY · 5-DAY LEAD","price":"QAR 620"}],"notes":["All options available from single fulfillment in Doha.","3 suppliers offer 10–15% bulk discount for orders over 100 units.","Bulk order qualifies for priority delivery (3–5 days).","GreenHub has lowest MOQ (25 units) to test before bulk.","Quote valid 24 hrs — one-click renewal available."]}}}\n\n' +
          'Live availability for the order shape:\n\n' +
          '{{results:{"title":"Order availability","rows":[{"label":"Suppliers in stock","value":"4","status":"high"},{"label":"Lead time","value":"10 days","status":"normal"},{"label":"Below MOQ","value":"1 supplier","status":"low"}]}}}\n\n' +
          'Per-unit pricing across the shortlist:\n\n' +
          '{{barChart:{"title":"Per-unit price","unit":"QAR","maxValue":900,"points":[{"label":"GreenHub","value":640},{"label":"Nexus Pro","value":480},{"label":"Workline","value":620},{"label":"Acer Direct","value":540}]}}}\n\n' +
          'How market pricing has moved this year:\n\n' +
          '{{lineChart:{"title":"Market average","unit":"QAR","maxValue":900,"points":[{"label":"Jan","value":820},{"label":"Mar","value":780},{"label":"May","value":740},{"label":"Jul","value":705},{"label":"Sep","value":690}]}}}\n\n' +
          'Vendor scorecard — newest reading highlighted:\n\n' +
          '{{reportCard:{"title":"Vendor scorecard","points":[{"x":1,"y":58},{"x":2,"y":63},{"x":3,"y":67},{"x":4,"y":72},{"x":5,"y":78,"latest":true}],"metrics":[{"label":"On-time","value":"96%"},{"label":"Defects","value":"0.4%"},{"label":"Lead time","value":"10d"},{"label":"NPS","value":"+48"}]}}}\n\n' +
          'Three ways to play it:\n\n' +
          '{{textOptions:{"title":"Decision tracks","subtitle":"Pick one before Friday","paragraphs":["Lock the lowest quote and ship in three weeks.","Split the order across two suppliers for resilience.","Wait two weeks for the rumoured price drop — risk: stock-out."],"bullets":["Bulk tier kicks in at 100 units","Warranty parity across all three"]}}}\n\n' +
          'When you are ready to act:\n\n' +
          '{{action:{"label":"Start a side-by-side comparison","action":"navigate_compare"}}}',
      },
    ],
    480,
  ),
];

const MOCK_CHATS: Chat[] = SEED.map((s) => s.chat);
const MOCK_MESSAGES: Record<string, ChatMessage[]> = Object.fromEntries(
  SEED.map((s) => [s.chat.id, s.messages]),
);

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

class MockChatRepository implements ChatRepository {
  async getChats(): Promise<Chat[]> {
    await delay(200);
    return [...MOCK_CHATS].sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
  }

  async getChatsPaginated(query: ChatsQuery): Promise<PaginatedResponse<Chat>> {
    await delay(200);
    let list = [...MOCK_CHATS].sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
    const search = query.search?.trim().toLowerCase();
    if (search) {
      list = list.filter(
        (c) =>
          (c.title ?? "").toLowerCase().includes(search) ||
          (c.lastMessagePreview ?? "").toLowerCase().includes(search),
      );
    }
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? 10;
    const totalCount = list.length;
    const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
    const start = (page - 1) * pageSize;
    return {
      data: list.slice(start, start + pageSize),
      meta: {
        currentPage: page,
        isFirstPage: page <= 1,
        isLastPage: page >= pageCount,
        previousPage: page <= 1 ? null : page - 1,
        nextPage: page >= pageCount ? null : page + 1,
        pageCount,
        totalCount,
      },
    };
  }

  async getMessages(chatId: string): Promise<ChatMessage[]> {
    await delay(200);
    return MOCK_MESSAGES[chatId] ?? [];
  }

  async sendMessageStream(
    message: string,
    chatId: string | null,
    _attachments: ChatAttachmentInput[] | undefined,
    cb: StreamCallbacks,
    signal?: AbortSignal,
    _extras?: ChatStreamExtras,
  ): Promise<void> {
    try {
      await delay(250);
      if (signal?.aborted) return;

      const nowIso = new Date().toISOString();
      const id = chatId ?? `mock-${Date.now()}`;
      if (!MOCK_CHATS.some((c) => c.id === id)) {
        MOCK_CHATS.push({
          id,
          title: message.slice(0, 40),
          createdAt: nowIso,
          updatedAt: nowIso,
          lastMessageAt: nowIso,
          lastMessagePreview: "",
        });
      }
      cb.onChatId(id);

      // Persist the user turn so reloading the chat from /my-conversations
      // shows it in the transcript.
      const userMsg: ChatMessage = {
        id: `${id}-u-${Date.now()}`,
        chatId: id,
        role: "user",
        text: message,
        timestamp: nowIso,
      };
      MOCK_MESSAGES[id] = [...(MOCK_MESSAGES[id] ?? []), userMsg];

      const reply = MOCK_REPLIES[Math.floor(Math.random() * MOCK_REPLIES.length)] ?? "";
      const words = reply.split(/(\s+)/);
      let acc = "";
      for (const w of words) {
        if (signal?.aborted) return;
        await delay(25 + Math.random() * 55);
        acc += w;
        cb.onDelta(w);
      }

      // Persist the assistant turn + bump chat preview so the list reflects
      // the latest exchange.
      const doneIso = new Date().toISOString();
      const assistantMsg: ChatMessage = {
        id: `${id}-a-${Date.now()}`,
        chatId: id,
        role: "assistant",
        text: acc,
        timestamp: doneIso,
      };
      MOCK_MESSAGES[id] = [...(MOCK_MESSAGES[id] ?? []), assistantMsg];
      const chat = MOCK_CHATS.find((c) => c.id === id);
      if (chat) {
        chat.lastMessageAt = doneIso;
        chat.updatedAt = doneIso;
        chat.lastMessagePreview = acc.replace(/\s+/g, " ").trim().slice(0, 120);
      }

      cb.onDone([]);
    } catch (err) {
      if (signal?.aborted) return;
      cb.onError(err instanceof Error ? err.message : "Mock stream failed");
    }
  }
}

// ── HTTP repository ──────────────────────────────────────────────────
// POST /api/v1/chat/stream returns concatenated JSON objects (no delimiter).
// We parse with a brace-counting scanner (doc 03). Auth token comes from
// tokenStore so the header matches what the rest of the app sends.

const CHAT_BASE = `${apiBase}/api/v1`;

function authHeader(): Record<string, string> {
  const t = tokenStore.get();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function parseJsonOrThrow(res: Response): Promise<unknown> {
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.text()).slice(0, 300); } catch { /* ignore */ }
    throw new Error(`HTTP ${res.status} ${res.statusText}${detail ? ` — ${detail}` : ""}`);
  }
  return res.json();
}

class HttpChatRepository implements ChatRepository {
  async getChats(): Promise<Chat[]> {
    const res = await fetch(`${CHAT_BASE}/chats`, { headers: authHeader() });
    const data = await parseJsonOrThrow(res);
    return (data as { data?: Chat[] }).data ?? (data as Chat[]);
  }

  async getChatsPaginated(query: ChatsQuery): Promise<PaginatedResponse<Chat>> {
    const params = new URLSearchParams();
    if (query.page) params.set("page", String(query.page));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));
    if (query.search) params.set("search", query.search);
    const qs = params.toString();
    const res = await fetch(`${CHAT_BASE}/chats${qs ? `?${qs}` : ""}`, {
      headers: authHeader(),
    });
    return (await parseJsonOrThrow(res)) as PaginatedResponse<Chat>;
  }

  async getMessages(chatId: string): Promise<ChatMessage[]> {
    const res = await fetch(`${CHAT_BASE}/chats/${encodeURIComponent(chatId)}/messages`, {
      headers: authHeader(),
    });
    const data = await parseJsonOrThrow(res);
    return (data as { data?: ChatMessage[] }).data ?? (data as ChatMessage[]);
  }

  async sendMessageStream(
    message: string,
    chatId: string | null,
    attachments: ChatAttachmentInput[] | undefined,
    cb: StreamCallbacks,
    signal?: AbortSignal,
    extras?: ChatStreamExtras,
  ): Promise<void> {
    let res: Response;
    try {
      res = await fetch(`${CHAT_BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({
          message,
          chatId,
          ...(attachments && attachments.length ? { attachments } : {}),
          ...(extras?.productId != null ? { productId: extras.productId } : {}),
          ...(extras?.variantId != null ? { variantId: extras.variantId } : {}),
        }),
        signal,
      });
    } catch (err) {
      if (signal?.aborted) return;
      cb.onError(err instanceof Error ? err.message : "Network error");
      return;
    }

    if (!res.ok || !res.body) {
      let detail = "";
      try { detail = (await res.text()).slice(0, 300); } catch { /* ignore */ }
      cb.onError(`HTTP ${res.status}${detail ? ` — ${detail}` : ""}`);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let doneEmitted = false;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Extract every complete top-level JSON object from the buffer.
        let i = 0;
        while (i < buffer.length) {
          while (i < buffer.length && /\s/.test(buffer[i]!)) i++;
          if (i >= buffer.length || buffer[i] !== "{") break;

          let depth = 0;
          let inStr = false;
          let esc = false;
          const start = i;
          for (; i < buffer.length; i++) {
            const c = buffer[i]!;
            if (esc) { esc = false; continue; }
            if (c === "\\") { esc = true; continue; }
            if (c === '"') { inStr = !inStr; continue; }
            if (inStr) continue;
            if (c === "{") depth++;
            else if (c === "}") {
              depth--;
              if (depth === 0) { i++; break; }
            }
          }
          if (depth !== 0) break; // incomplete — wait for more bytes

          const slice = buffer.slice(start, i);
          buffer = buffer.slice(i);
          i = 0;

          try {
            const obj = JSON.parse(slice) as {
              chatId?: string;
              text?: string;
              done?: boolean;
              insights?: ChatInsight[];
              error?: string;
            };
            if (obj.chatId) cb.onChatId(obj.chatId);
            if (obj.text != null) cb.onDelta(obj.text);
            if (obj.error) cb.onError(obj.error);
            if (obj.done) { doneEmitted = true; cb.onDone(obj.insights ?? []); }
          } catch {
            // Skip malformed event; keep reading.
          }
        }
      }
      if (!doneEmitted) cb.onDone([]);
    } catch (err) {
      if (signal?.aborted) return;
      cb.onError(err instanceof Error ? err.message : "Stream read failed");
    }
  }
}

// ── Resolver ─────────────────────────────────────────────────────────
// Switch via `VITE_CHAT_MOCK=true` for local/demo without a backend.
// Defaults to HTTP when a backend base URL is configured.
//
// In dev (`import.meta.env.DEV`), the seeded mock chats are merged into the
// real list and any chat whose id starts with `seed-` is served from the
// mock store — so the widget showcase is browseable without flipping env
// vars or polluting the real DB. Disable with `VITE_CHAT_SEEDS=false`.

const mockInstance = new MockChatRepository();
const httpInstance = new HttpChatRepository();

function useMock(): boolean {
  return import.meta.env.VITE_CHAT_MOCK === "true" || !apiBase;
}

function showSeeds(): boolean {
  if (import.meta.env.VITE_CHAT_SEEDS === "false") return false;
  return import.meta.env.DEV === true;
}

function isSeedId(id: string): boolean {
  return id.startsWith("seed-");
}

export const chatRepository: ChatRepository = {
  async getChats() {
    if (useMock()) return mockInstance.getChats();
    const real = await httpInstance.getChats().catch(() => [] as Chat[]);
    if (!showSeeds()) return real;
    const seeds = await mockInstance.getChats();
    return [...real, ...seeds];
  },
  async getChatsPaginated(query: ChatsQuery) {
    if (useMock()) return mockInstance.getChatsPaginated(query);
    const result = await httpInstance.getChatsPaginated(query);
    if (showSeeds()) {
      const seeds = await mockInstance.getChatsPaginated({ pageSize: 100 });
      const search = query.search?.trim().toLowerCase();
      const filteredSeeds = search
        ? seeds.data.filter(
            (c) =>
              (c.title ?? "").toLowerCase().includes(search) ||
              (c.lastMessagePreview ?? "").toLowerCase().includes(search),
          )
        : seeds.data;
      result.data = [...result.data, ...filteredSeeds];
      result.meta.totalCount = (result.meta.totalCount ?? 0) + filteredSeeds.length;
    }
    return result;
  },
  getMessages(id) {
    if (useMock() || isSeedId(id)) return mockInstance.getMessages(id);
    return httpInstance.getMessages(id);
  },
  sendMessageStream(m, id, att, cb, s, extras) {
    if (useMock() || (id && isSeedId(id))) {
      return mockInstance.sendMessageStream(m, id, att, cb, s, extras);
    }
    return httpInstance.sendMessageStream(m, id, att, cb, s, extras);
  },
};
